import { graphql, Link } from 'gatsby';
import renderHTML from 'htmr';
import ReactImageZoom from 'react-image-zoom';
import BodyClassName from 'react-body-classname';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import React, { useState, useCallback, useEffect, useMemo } from 'react';

import { formatTitle } from '../../utils/shop';
import LoadingGif from '../../images/gif/loading.gif';
import ShopProduct from '../../components/Shop/ShopProduct';
import ShopProductTitle from '../../components/Shop/ShopProductTitle';
import SocialShare from '../../components/Shop/SocialShare';
import { getColorHexFromName } from '../../constants/colors';
import { useAddItemToCart, useCartItems } from '../../context/storeContext';
import VideoWatcher from '../../components/Shop/VideoWatcher';
import ProductReview from '../../components/Shop/ProductReview';
import MagnifierButton from '../../components/Shop/MagnifierButton';
import DefaultLayoutComponent from '../layouts/DefaultLayoutComponent';
import ProductReviewForm from '../../components/Form/ProductReviewForm';
import CurrencySwitcher from '../../components/Header/currencyswitcher/CurrencySwitcher';
import useUpdatedProductPrices, { useSingleUpdatedProductPrice } from '../../hooks/useUpdatedProductPrice';
import { DescriptionLoader, PriceLoader, TitleLoader } from '../../utils/loader';
import QuantityDropdown from '../../components/Shop/QuantityDropdown';
import ShareablePackageSelector from '../../components/Shop/ShareablePackageSelector';
import {
  formatCartPackageAttribute,
  getColouringBookPackageOptions,
  getIllustratedPackageOptions,
  getProductDisplayPrice,
  getProductTierScheme,
  getShareableBulkProductHandle,
  getShareableBulkProductHandleAlternates,
  getShareableBulkProductTitleSearch,
  getShareablePackageOptions,
  getTierPrice,
  findShareableBulkProductByTitle,
  qualifiesForBulkDiscount,
} from '../../utils/discounts';
import { AuthorSection } from '../../components/BookChapters/Author-section';
import { useShopifyCurrencies } from '../../hooks/useShopifyCountries';
import { useProductCurrencies } from '../../hooks/useProductCountryAvailability';

const SUCCESS_MESSAGE = 'Product Added to Cart Successfully';
// const FAILED_MESSAGE = 'Failed to Add Item to Cart';

const STOREFRONT_API_URL = `https://lineage-journey.myshopify.com/api/2023-10/graphql.json`;

const fetchProductVariantByHandle = async (handle) => {
  if (!handle) return null;

  const query = `
    query {
      productByHandle(handle: "${handle}") {
        title
        variants(first: 1) {
          edges {
            node {
              id
              availableForSale
              price {
                amount
              }
            }
          }
        }
      }
    }
  `;

  const response = await fetch(STOREFRONT_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': process.env.GATSBY_SHOPIFY_STORE_FRONT_ACCESS_TOKEN,
    },
    body: JSON.stringify({ query }),
  });

  const { data } = await response.json();
  const variant = data?.productByHandle?.variants?.edges?.[0]?.node;

  if (!variant) return null;

  return {
    shopifyId: variant.id,
    availableForSale: variant.availableForSale,
    price: variant.price?.amount,
  };
};

const fetchProductVariantByTitleSearch = async (titleSearch) => {
  if (!titleSearch) return null;

  const escaped = titleSearch.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  const query = `
    query {
      products(first: 5, query: "title:*${escaped}*") {
        edges {
          node {
            title
            handle
            variants(first: 1) {
              edges {
                node {
                  id
                  availableForSale
                  price {
                    amount
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const response = await fetch(STOREFRONT_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token':
        process.env.GATSBY_SHOPIFY_STORE_FRONT_ACCESS_TOKEN,
    },
    body: JSON.stringify({ query }),
  });

  const { data } = await response.json();
  const products = data?.products?.edges?.map((edge) => edge.node) || [];
  const match =
    products.find((product) => product?.title?.includes(titleSearch)) || products[0];
  const variant = match?.variants?.edges?.[0]?.node;

  if (!variant) return null;

  return {
    shopifyId: variant.id,
    availableForSale: variant.availableForSale,
    price: variant.price?.amount,
  };
};

const fetchProductAvailabilityByCountry = async (handle, countryCode) => {
  const query = `
    query @inContext(country: ${countryCode}) {
      productByHandle(handle: "${handle}") {
        id
        title
        variants(first: 50) {
          edges {
            node {
              id
              title
              availableForSale
              quantityAvailable
            }
          }
        }
      }
    }
  `;

  const response = await fetch(STOREFRONT_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': process.env.GATSBY_SHOPIFY_STORE_FRONT_ACCESS_TOKEN,
    },
    body: JSON.stringify({ query }),
  });

  const data = await response.json();

  // if (errors) {
  //   console.error(errors);
  //   return null;
  // }

  return data;
};

const getTotalColorsAndSizeFromVariants = (variants) => {
  let totalColorList = [];
  let totalSizeList = [];
  let totalImage = [];

  let totalImageDetails = [];

  variants.forEach((variant, vIndex) => {
    const selectedOptions = variant.selectedOptions;
    selectedOptions.forEach((option) => {
      // If the totalColorList doesnt include the color then
      if (option.name === 'Color') {
        if (!totalColorList.includes(option.value)) {
          totalColorList.push(option.value);
          totalImage.push(variant?.image.gatsbyImageData?.images?.fallback?.src);
          totalImageDetails.push({
            url: variant?.image?.gatsbyImageData?.images?.fallback?.src,
            color: option.value,
            id: vIndex,
          });
        }
      }

      if (option.name === 'Size') {
        if (!totalSizeList.includes(option.value)) {
          totalSizeList.push(option.value);
        }
      }
    });
  });

  return { totalColorList, totalSizeList, totalImage, totalImageDetails };
};

const findMatchingVariant = (variants, color, size, { hasColorOption, hasSizeOption }) => {
  return variants.find((variant) => {
    const options = variant.selectedOptions || [];
    const variantColor = options.find((opt) => opt.name === 'Color');
    const variantSize = options.find((opt) => opt.name === 'Size');

    if (hasSizeOption) {
      if (!size || !variantSize || variantSize.value !== size) return false;
    }

    if (hasColorOption) {
      if (!color || !variantColor || variantColor.value !== color) return false;
    }

    return true;
  });
};

const formatSelectionLabel = (size, color, hasSizeOption, hasColorOption) => {
  const parts = [];
  if (hasSizeOption && size) parts.push(size);
  if (hasColorOption && color) parts.push(color);

  if (parts.length) return parts.join(' / ');
  if (hasSizeOption && hasColorOption) return 'the selected size and color';
  if (hasSizeOption) return 'the selected size';
  if (hasColorOption) return 'the selected color';
  return 'the selected options';
};

const checkVariantAvailability = (product, color, size, optionFlags) => {
  if (!product?.variants || product.variants.length === 0) {
    return { available: false, message: 'Product unavailable' };
  }

  // If product has no color/size options (single variant products)
  if (product.variants.length === 1) {
    const variant = product.variants[0];
    return {
      available: variant.availableForSale,
      message: variant.availableForSale ? '' : 'Out of stock',
      variant: variant,
    };
  }

  const selectedVariant = findMatchingVariant(product.variants, color, size, optionFlags);

  if (!selectedVariant) {
    return { available: false, message: 'Variant not found' };
  }

  return {
    available: selectedVariant.availableForSale,
    message: selectedVariant.availableForSale ? '' : 'Out of stock',
    variant: selectedVariant,
  };
};

const ProductPageComponent = (props) => {
  const {
    data: { productDetails, relatedProducts, shareableBulkProducts },
    pageContext: { categoryName },
  } = props;

  const addItemToCart = useAddItemToCart();
  const cartItems = useCartItems();

  const isIBooksCategory = categoryName === 'iBooks';
  const [productData] = productDetails?.edges;
  const [product, setProduct] = useState(productData.node);

  const productHandle = product.handle;
  const productPriceRange = product.priceRange;
  const { priceRange, loading } = useSingleUpdatedProductPrice(productHandle, productPriceRange);
  const { products: relatedProduct, loading: newloading } = useUpdatedProductPrices(relatedProducts);

  const currencyCode = priceRange?.minVariantPrice?.currencyCode;

  fetchProductAvailabilityByCountry(productHandle, 'GB');

  const {
    currencies: availableCurrencies,
    isAvailableInMultipleCountries,
    totalCountries,
    loading: currenciesLoading,
    error: currenciesError,
  } = useProductCurrencies(productHandle);

  const shouldShowCurrencySwitcher = useMemo(() => {
    // Only show if we have multiple currencies available
    return availableCurrencies && availableCurrencies.length > 1;
  }, [availableCurrencies]);

  const tierScheme = getProductTierScheme(product?.handle, product?.title);
  const shareableBulkProductNodes = useMemo(
    () => shareableBulkProducts?.edges?.map(({ node }) => node).filter(Boolean) || [],
    [shareableBulkProducts]
  );

  const bulkProductsByHandle = useMemo(() => {
    const map = {};
    shareableBulkProductNodes.forEach((node) => {
      if (node?.handle) {
        map[node.handle] = node;
      }
    });
    return map;
  }, [shareableBulkProductNodes]);

  const resolveShareableBulkVariant = async (minQty) => {
    const bulkHandle = getShareableBulkProductHandle(minQty);
    const cachedByTitle = findShareableBulkProductByTitle(shareableBulkProductNodes, minQty);
    const cachedVariant = cachedByTitle?.variants?.[0] || bulkProductsByHandle[bulkHandle]?.variants?.[0];

    if (cachedVariant?.shopifyId) {
      return cachedVariant;
    }

    if (bulkHandle) {
      const byHandle = await fetchProductVariantByHandle(bulkHandle);
      if (byHandle?.shopifyId) return byHandle;
    }

    const alternateHandles = getShareableBulkProductHandleAlternates(minQty);
    for (const alternateHandle of alternateHandles) {
      const byAlternateHandle = await fetchProductVariantByHandle(alternateHandle);
      if (byAlternateHandle?.shopifyId) return byAlternateHandle;
    }

    const titleSearch = getShareableBulkProductTitleSearch(minQty);
    if (titleSearch) {
      return fetchProductVariantByTitleSearch(titleSearch);
    }

    return null;
  };

  const packageOptions = useMemo(() => {
    if (tierScheme === 'shareable') return getShareablePackageOptions();
    if (tierScheme === 'illustrated') return getIllustratedPackageOptions();
    if (tierScheme === 'colouring') return getColouringBookPackageOptions();
    return [];
  }, [tierScheme]);
  const [selectedPackageId, setSelectedPackageId] = useState('single');

  const selectedPackage = useMemo(
    () => packageOptions.find((pkg) => pkg.id === selectedPackageId) || packageOptions[0],
    [packageOptions, selectedPackageId]
  );

  const isShareableProduct = tierScheme === 'shareable';
  const isIllustratedProduct = tierScheme === 'illustrated';
  const isColouringProduct = tierScheme === 'colouring';
  const hasPackageSelector = isShareableProduct || isIllustratedProduct || isColouringProduct;
  const usesSteppedQuantityDropdown = isIllustratedProduct || isColouringProduct;
  const isShareableBulkPackageSelection =
    isShareableProduct && selectedPackage && !selectedPackage.useBasePrice;
  const usesShareableBulkProduct =
    isShareableBulkPackageSelection && !!selectedPackage.bulkProductHandle;

  const handlePackageSelect = (pkg) => {
    setSelectedPackageId(pkg.id);
    setQuantity(pkg.useBasePrice ? 1 : pkg.minQty);
    setErrorMsg('');
  };

  const syncPackageToQuantity = (qty) => {
    const matching = packageOptions.find((pkg) => qty >= pkg.minQty && qty <= pkg.maxQty);
    if (matching && matching.id !== selectedPackageId) {
      setSelectedPackageId(matching.id);
    }
  };

  const normalizeQuantity = (value) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 1) return 1;
    if (isShareableProduct && selectedPackage?.useBasePrice) {
      return Math.min(numValue, 31);
    }
    return numValue;
  };

  const applyTypedQuantity = (value) => {
    const nextQty = normalizeQuantity(value);
    setQuantity(nextQty);
    if (usesSteppedQuantityDropdown) {
      syncPackageToQuantity(nextQty);
    }
    return nextQty;
  };

  useEffect(() => {
    if (priceRange && !loading) {
      setProduct((prev) => ({
        ...prev,
        priceRange,
      }));
    }
  }, [priceRange, loading]);

  const hasVariants = product?.variants.length > 1;

  const { totalColorList, totalSizeList, totalImage, totalImageDetails } = getTotalColorsAndSizeFromVariants(
    product.variants
  );

  // TODO: Try using gatsbyImage
  const [activeImage, setActiveImage] = useState(product?.featuredImage?.gatsbyImageData);
  const [size, setSize] = useState();
  const [color, setColor] = useState();
  const [tabId, setTabId] = useState(1);
  const [quantity, setQuantity] = useState(1);
  const [errorMsg, setErrorMsg] = useState('');
  const [selectedImageId, setImageId] = useState(null);
  // const [cart, setCart] = useContext(AppContext);

  const [zoomImageWrapperWidth, setZoomImageWrapperWidth] = useState(null);
  const zoomImageWrapperDiv = useCallback((node) => {
    if (node !== null) {
      setZoomImageWrapperWidth(node.getBoundingClientRect().width);
    }
  }, []);

  const getSelectedVariantProductIdByColorAndSize = (hasVariants, optionFlags) => {
    if (!hasVariants) {
      const [selectedVariant] = product?.variants;
      return selectedVariant.shopifyId;
    }

    const { hasColorOption, hasSizeOption } = optionFlags;

    if (hasColorOption && !color) {
      throw new Error('Please choose a color.');
    }

    if (hasSizeOption && !size) {
      throw new Error('Please choose a size.');
    }

    const selectedVariant = findMatchingVariant(product.variants, color, size, optionFlags);

    return selectedVariant ? selectedVariant.shopifyId : null;
  };

  const variantOptionFlags = useMemo(
    () => ({
      hasColorOption: totalColorList.length > 0,
      hasSizeOption: totalSizeList.length > 0,
    }),
    [totalColorList.length, totalSizeList.length]
  );

  const isOutOfStock = useMemo(() => {
    if (!hasVariants) {
      return !product?.variants[0]?.availableForSale;
    }

    // Only check availability if user has made selections for multi-variant products
    if (hasVariants && (color || size)) {
      const stockCheck = checkVariantAvailability(product, color, size, variantOptionFlags);
      return !stockCheck.available;
    }

    // For multi-variant products with no selection yet, don't show out of stock
    return false;
  }, [product, color, size, hasVariants, variantOptionFlags]);

  const images = !!product?.media?.length
    ? product?.media.map((elm, index) => ({ url: elm.image.gatsbyImageData?.images?.fallback?.src, id: index }))
    : totalImageDetails;

  const renderProductAttributes = (totalColorList, totalSizeList, totalImageDetails) => {
    const getSelectedColorImageUrl = (colorName) => {
      const image = totalImageDetails.find((elm) => elm.color === colorName);
      return image.url;
    };
    return (
      <div className="product-attributes">
        {!!totalColorList.length && (
          <div className={`product-sku product-sku-color ${color ? 'selected' : ''}`}>
            <div className="product-sku-title">Color: {color}</div>
            <ul className="product-sku-list">
              {totalColorList.map((colorName, index) => (
                <li
                  className={`product-sku-item ${color === colorName ? 'selected' : ''}`}
                  style={{ background: getColorHexFromName(colorName) }}
                  onClick={() => {
                    setColor(colorName);
                    setActiveImage({ images: { fallback: { src: getSelectedColorImageUrl(colorName) } } });
                  }}
                  key={`color-choice-${index}`}
                ></li>
              ))}
            </ul>
          </div>
        )}
        {!!totalSizeList.length && (
          <div className="product-sku product-sku-size">
            <div className="product-sku-title">Size:</div>
            <ul className="product-sku-list">
              {totalSizeList.map((sizeName, index) => (
                <li
                  key={index}
                  className={`product-sku-item ${size === sizeName ? 'selected' : ''}`}
                  onClick={() => {
                    setSize(sizeName);
                  }}
                >
                  <span className="product-sku-item-type">{sizeName}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };
  const notifyOnItemAddedToCart = (isSuccess, errorMessage) => {
    if (isSuccess) {
      toast.success(SUCCESS_MESSAGE, {
        hideProgressBar: true,
        position: 'bottom-center',
      });
    } else {
      toast.error(errorMessage || 'This product is not available. Please choose another option.', {
        hideProgressBar: true,
        position: 'bottom-center',
        duration: 6000,
      });
    }
  };

  const addToCartHandler = async () => {
    const { hasColorOption, hasSizeOption } = variantOptionFlags;
    const selectionLabel = formatSelectionLabel(size, color, hasSizeOption, hasColorOption);

    try {
      let variantProductId;
      let cartQuantity;
      let customAttributes = [];

      if (usesShareableBulkProduct) {
        const bulkHandle = getShareableBulkProductHandle(selectedPackage.minQty);

        if (!bulkHandle) {
          setErrorMsg('This bulk package is not available yet.');
          return;
        }

        const bulkVariant = await resolveShareableBulkVariant(selectedPackage.minQty);

        if (!bulkVariant?.shopifyId) {
          setErrorMsg(
            'Bulk box product not found in Shopify. Check the product handle in Admin and restart the site build.'
          );
          return;
        }

        if (!bulkVariant.availableForSale) {
          const msg = 'This bulk package is currently out of stock.';
          setErrorMsg(msg);
          notifyOnItemAddedToCart(false, msg);
          return;
        }

        variantProductId = bulkVariant.shopifyId;
        cartQuantity = 1;
        customAttributes = [
          {
            key: 'package',
            value: formatCartPackageAttribute(selectedPackage),
          },
        ];
      } else {
        // For products without variants, skip color/size check
        if (!hasVariants) {
          const [defaultVariant] = product.variants;
          if (!defaultVariant.availableForSale) {
            const msg = 'This product is currently out of stock.';
            setErrorMsg(msg);
            notifyOnItemAddedToCart(false, msg);
            return;
          }
        } else {
          if (hasSizeOption && !size) {
            setErrorMsg('Please choose a size.');
            return;
          }

          if (hasColorOption && !color) {
            setErrorMsg('Please choose a color.');
            return;
          }

          // For products with variants, check availability
          const stockCheck = checkVariantAvailability(product, color, size, variantOptionFlags);
          if (!stockCheck.available) {
            const msg =
              stockCheck.message === 'Out of stock'
                ? `This product is out of stock for ${selectionLabel}.`
                : `This product is not available for ${selectionLabel}. Please choose another option.`;
            setErrorMsg(msg);
            notifyOnItemAddedToCart(false, msg);
            return;
          }
        }

        variantProductId = getSelectedVariantProductIdByColorAndSize(hasVariants, variantOptionFlags);

        if (!variantProductId) {
          console.error('Missing variantProductId');
          const msg = `This product is not available for ${selectionLabel}. Please choose another option.`;
          setErrorMsg(msg);
          notifyOnItemAddedToCart(false, msg);
          return;
        }

        const basePrice = getProductDisplayPrice(priceRange);
        const numericQuantity = parseInt(quantity, 10) || 1;
        const discountedPrice = tierScheme ? getTierPrice(tierScheme, basePrice, numericQuantity) : basePrice;
        const hasDiscount = tierScheme && qualifiesForBulkDiscount(tierScheme, numericQuantity);

        cartQuantity = numericQuantity;
        customAttributes = [
          ...(color ? [{ key: 'color', value: color }] : []),
          ...(size ? [{ key: 'size', value: size }] : []),
          ...(hasPackageSelector && selectedPackage
            ? [
                {
                  key: 'package',
                  value: formatCartPackageAttribute(selectedPackage),
                },
              ]
            : []),
          ...(hasDiscount
            ? [
                { key: '_discountedPrice', value: discountedPrice },
                { key: '_originalPrice', value: basePrice },
              ]
            : []),
        ];
      }

      const addToCartParams = {
        variantId: variantProductId,
        quantity: cartQuantity,
        attributes: customAttributes,
      };

      setErrorMsg('');
      const isItemAddedSuccessfully = await addItemToCart(addToCartParams);
      if (!isItemAddedSuccessfully) {
        const msg =
          'Unable to add this product to cart. It may not be published for online sale yet — please check Shopify sales channel settings.';
        setErrorMsg(msg);
        notifyOnItemAddedToCart(false, msg);
        return;
      }
      notifyOnItemAddedToCart(true);
    } catch (error) {
      setErrorMsg(error.message || 'An error occurred while adding item to cart.');
    }
  };

  const displayPrice = useMemo(() => {
    if (!loading && priceRange) {
      const basePrice = getProductDisplayPrice(priceRange);
      if (tierScheme) {
        return getTierPrice(tierScheme, basePrice, quantity);
      }
      return basePrice;
    }
    return null;
  }, [loading, priceRange, quantity, tierScheme]);
  const ibookUrl = isIBooksCategory ? product?.metafields?.find((item) => item.key === 'ibook_url').value : '';

  const trailerVideoLink = !!product.metafields.length
    ? product?.metafields?.find((item) => item.key === 'trailer_video')
    : '';
  const trailerVidoeLinkUrl = (trailerVideoLink && trailerVideoLink?.value) || '';

  const seoData = {
    title: product?.title,
    metaDesc: product.description,
    opengraphDescription: product.description,
    twitterDescription: product.description,
    opengraphTitle: product?.title,
    twitterTitle: product?.title,
    opengraphImage: {
      sourceUrl: activeImage?.images?.fallback.src,
    },
    twitterImage: {
      sourceUrl: activeImage?.images?.fallback.src,
    },
  };

  return (
    <BodyClassName className="body-dark page-product">
      <DefaultLayoutComponent seo={seoData}>
        <ToastContainer autoClose={3000} closeOnClick pauseOnHover pauseOnFocusLoss limit={2} />
        <div className="site-main">
          <div className="wrapper wrapper-xl pd-x-md">
            <div className="section bg-light pd-t-0 product">
              <div className="wrapper wrapper-lg pd-x-sm">
                <div className="breadcrumb pd-x-sm"></div>
                <div className="product-single columns d-flex pd-x-sm">
                  <div className="cols cols-2 product-image pd-x-0">
                    <div className="product-preview" ref={zoomImageWrapperDiv}>
                      {
                        <ReactImageZoom
                          img={activeImage?.images?.fallback.src}
                          height={385}
                          width={385}
                          zoomPosition="original"
                        />
                      }
                      <MagnifierButton className="icon-magnifier" image={activeImage?.images?.fallback.src} />
                    </div>
                    <div className="product-slick">
                      {images.map((imageDetail, index) => (
                        <div
                          key={index}
                          className={`product-slick-list ${imageDetail.id === selectedImageId ? 'active' : ''}`}
                          onClick={() => {
                            setActiveImage({ images: { fallback: { src: imageDetail.url } } });
                            setImageId(imageDetail.id);
                            setColor(imageDetail.color);
                          }}
                        >
                          <LazyLoadImage src={imageDetail.url} alt={imageDetail?.altText || ''} effect="blur" />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="cols cols-2 product-info pd-x-0">
                    <h2 className="product-title">
                      {loading || !displayPrice ? (
                        <TitleLoader />
                      ) : (
                        <ShopProductTitle title={product?.title} promoClassName="product-title-promo" />
                      )}
                    </h2>
                    <h4 className="product-price">
                      {loading || !displayPrice ? (
                        <PriceLoader />
                      ) : (
                        <CurrencySwitcher currencyCode={currencyCode} price={displayPrice} caller="product" />
                      )}
                    </h4>
                    <div className="product-description">
                      {product.description &&
                        product.description.length > 0 &&
                        (loading || !displayPrice ? <DescriptionLoader /> : renderHTML(product.description))}
                    </div>
                    {hasPackageSelector && (
                      <ShareablePackageSelector
                        packages={packageOptions}
                        selectedPackageId={selectedPackageId}
                        onSelect={handlePackageSelect}
                        basePrice={getProductDisplayPrice(priceRange)}
                      />
                    )}
                    {renderProductAttributes(totalColorList, totalSizeList, totalImageDetails)}
                    {isIBooksCategory ? (
                      <div className="product-action">
                        <div className="product-add-cart">
                          <a href={ibookUrl} className="btn btn-sm btn-primary-ii">
                            Buy on Apple Books
                          </a>
                        </div>
                      </div>
                    ) : (
                      <div className="product-action">
                        {isShareableBulkPackageSelection ? (
                          <div className="product-quantity-locked">
                            <span className="product-quantity-locked-label">Quantity:</span>
                            <span className="product-quantity-locked-value">
                              {quantity} books ({selectedPackage.packageLabel})
                            </span>
                          </div>
                        ) : (
                          <div className="product-quantity-picker">
                            {usesSteppedQuantityDropdown ? (
                              <QuantityDropdown
                                value={quantity}
                                minQty={selectedPackage?.minQty || 1}
                                maxQty={selectedPackage?.maxQty}
                                step={selectedPackage?.quantityStep || 1}
                                onChange={applyTypedQuantity}
                              />
                            ) : (
                              <span className="cart-number">
                                <span
                                  className="cart-minus"
                                  onClick={() => applyTypedQuantity((parseInt(quantity, 10) || 1) - 1)}
                                >
                                  -
                                </span>
                                <input
                                  type="number"
                                  value={quantity}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '') {
                                      setQuantity('');
                                      return;
                                    }
                                    const numValue = parseInt(value, 10);
                                    if (isNaN(numValue)) return;
                                    setQuantity(numValue);
                                  }}
                                  onBlur={(e) => {
                                    applyTypedQuantity(e.target.value);
                                  }}
                                  min="1"
                                  max={isShareableProduct && selectedPackage?.useBasePrice ? 31 : undefined}
                                />
                                <span
                                  className="cart-plus"
                                  onClick={() => applyTypedQuantity((parseInt(quantity, 10) || 0) + 1)}
                                >
                                  +
                                </span>
                              </span>
                            )}
                          </div>
                        )}
                        <div className="product-add-cart">
                          <a
                            className={`btn btn-sm btn-primary-ii ${isOutOfStock ? 'btn-disabled' : ''}`}
                            onClick={isOutOfStock ? null : addToCartHandler}
                            style={{
                              opacity: isOutOfStock ? 0.5 : 1,
                              cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {isOutOfStock ? 'Out of Stock' : 'Add To Cart'}
                          </a>

                          {false && (
                            <div className="loader-overlay-container">
                              <img src={LoadingGif} alt="" />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="error-container">{!!errorMsg && <li className="error-msg">{errorMsg}</li>}</div>
                    {product?.sku && (
                      <div className="product-sku-number">
                        SKU: <span className="product-sku-number-unit">{product?.sku}</span>
                      </div>
                    )}
                    <div className="product-sku-category">
                      Category:{}
                      {product?.productCategories?.nodes.map((c) => (
                        <Link to={c.uri} key={c.id} className="product-sku-category-type">
                          {c.name}
                        </Link>
                      ))}
                    </div>
                    <SocialShare title={product?.title} />
                  </div>
                </div>

                {!!trailerVidoeLinkUrl ? (
                  <div className="product_video">
                    <VideoWatcher url={trailerVidoeLinkUrl} />
                  </div>
                ) : null}

                <div className="product-detail pd-x-sm">
                  {(product?.handle === 'the-illustrated-great-controversy-pre-order' ||
                    product?.handle === 'the-great-controversy') && (
                    <div
                      style={{
                        paddingTop: '40px',
                        paddingBottom: '10px',
                        backgroundColor: 'white',
                      }}
                      className={`wrapper wrapper-xl`}
                    >
                      <AuthorSection classname={'author-section-no-padding'} />
                    </div>
                  )}

                  <div className="product-detail-tabs">
                    <ul className="tab-head">
                      <li className={`tab-item ${tabId === 1 ? 'active' : ''}`} onClick={() => setTabId(1)}>
                        <a>Additional Info</a>
                      </li>
                      <li className={`tab-item ${tabId === 2 ? 'active' : ''}`} onClick={() => setTabId(2)}>
                        <a>
                          Review{' '}
                          <span className="review-count">
                            ({product.reviews && product.reviews.edges ? product.reviews.edges.length : 0})
                          </span>
                        </a>
                      </li>
                    </ul>
                    <div className="tab-body">
                      <div className={`tab-content ${tabId === 1 ? '' : 'd-none'}`}>
                        <ul className="product-specifications">
                          {!!totalColorList.length &&
                            [
                              { name: 'Color', options: totalColorList },
                              { name: 'Size', options: totalSizeList },
                            ].map((productProperty, index) => (
                              <li key={index} className="product-property">
                                <span className="pp-title">{productProperty.name}</span>
                                <span className="pp-description">{productProperty.options.join(', ')}</span>
                              </li>
                            ))}
                        </ul>
                      </div>
                      <div className={`tab-content ${tabId === 2 ? '' : 'd-none'}`}>
                        <div className="product-review columns d-flex">
                          <div className="cols cols-2 customer-reviews pd-x-0">
                            {product?.reviews &&
                              product?.reviews?.edges &&
                              product?.reviews?.edges.map((review) => (
                                <ProductReview key={review?.node?.id} review={review} />
                              ))}
                          </div>

                          <div className="cols cols-2 add-reviews pd-x-0">
                            <ProductReviewForm productId={product.productId} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {!!relatedProduct?.length && (
                  <div className="related-products pd-x-sm">
                    <h4>Related Products</h4>
                    <div className="product-list d-flex">
                      {relatedProduct?.map((p, index) => (
                        <ShopProduct
                          loading={newloading}
                          className="list-item"
                          key={index}
                          product={{ ...p.node, url: formatTitle(p.node.title) }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DefaultLayoutComponent>
    </BodyClassName>
  );
};

export const query = graphql`
  query MyProductPageQuery($title: String!, $categoryName: String) {
    productDetails: allShopifyProduct(sort: { fields: createdAt, order: DESC }, filter: { title: { eq: $title } }) {
      edges {
        node {
          tags
          handle
          title
          description: descriptionHtml
          featuredImage {
            gatsbyImageData
            width
            height
          }
          media {
            ... on ShopifyMediaImage {
              image {
                gatsbyImageData
              }
            }
          }
          status
          createdAt
          priceRange: priceRangeV2 {
            maxVariantPrice {
              amount
              currencyCode
            }
            minVariantPrice {
              amount
              currencyCode
            }
          }
          variants {
            availableForSale
            image {
              gatsbyImageData
            }
            price
            id
            selectedOptions {
              name
              value
            }
            shopifyId
            title
            availableForSale
            displayName
            sku
          }
          metafields {
            value
            key
          }
        }
      }
    }

    relatedProducts: allShopifyProduct(
      sort: { fields: createdAt, order: DESC }
      filter: { tags: { eq: $categoryName }, title: { ne: $title } }
      limit: 3
    ) {
      edges {
        node {
          title
          handle
          featuredImage {
            gatsbyImageData
            width
            height
          }
          media {
            ... on ShopifyMediaImage {
              image {
                gatsbyImageData
              }
            }
          }
          status
          priceRange: priceRangeV2 {
            maxVariantPrice {
              amount
            }
            minVariantPrice {
              amount
            }
          }
        }
      }
    }

    shareableBulkProducts: allShopifyProduct(
      filter: {
        handle: {
          in: [
            "the-great-controversy-full-colour-shareable-edition-1x-box-32-books",
            "the-great-controversy-full-colour-shareable-edition-2x-boxes-64-books",
            "the-great-controversy-full-colour-shareable-edition-4x-boxes-128-books",
            "the-great-controversy-full-colour-shareable-edition-half-pallet-640-books",
            "the-great-controversy-full-colour-shareable-edition-1x-pallet-1280-books",
            "the-great-controversy-full-colour-shareable-edition-2x-pallets-2560-books",
            "the-great-controversy-full-colour-shareable-edition-2x-pallet-2560-books",
            "the-great-controversy-full-colour-shareable-edition-4x-pallets-5120-books",
            "the-great-controversy-full-colour-shareable-edition-8x-pallets-10240-books"
          ]
        }
      }
    ) {
      edges {
        node {
          handle
          title
          priceRange: priceRangeV2 {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          variants {
            shopifyId
            availableForSale
            price
          }
        }
      }
    }
  }
`;

export default ProductPageComponent;
